import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import type { TypeQuote, TypeQuoteBulkSkip, TypeQuoteStatus } from '@/types/commercial/types.commercial.quote';
import type { TypeDocumentPdf } from '@/types/document/types.document.print';
import { PDF_REQUEST_TIMEOUT_MS } from '@/config/document/document.pdf.config';
import type { TypeAppDispatch } from '@/store/store';
import { dropById, upsertById } from '@/store/api/base/api.cache';

type TypeQuoteBulkStatusResult = {
    updated: TypeQuote[];
    unchanged: string[];
    skipped: TypeQuoteBulkSkip[];
};

type TypeQuoteBulkRemoveResult = {
    removed: string[];
    skipped: TypeQuoteBulkSkip[];
};

const PAGE_SIZE = 200;
const MAX_PAGES = 40;

const byRecent = (a: TypeQuote, b: TypeQuote) => b.updatedAt.localeCompare(a.updatedAt);

export const quoteApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        readQuotePdf: build.mutation<TypeDocumentPdf, string>({
            query: (id) => ({ url: `/quotes/${id}/pdf`, timeout: PDF_REQUEST_TIMEOUT_MS }),
        }),

        readQuoteDraftPdf: build.mutation<TypeDocumentPdf, { quote: TypeQuote; id: string | null }>({
            query: ({ quote, id }) => ({ url: '/quotes/draft/pdf', method: 'post', data: { quote: toInput(quote), id: id ?? '' }, timeout: PDF_REQUEST_TIMEOUT_MS }),
        }),

        listQuotes: build.query<TypeQuote[], void>({
            queryFn: async () => {
                const walked = await walkPages<TypeQuote>({
                    url: '/quotes',
                    fallback: 'Non è stato possibile caricare i preventivi',
                    params: { sort: 'updated' },
                    pageSize: PAGE_SIZE,
                    maxPages: MAX_PAGES,
                });
                if ('error' in walked) return walked;
                const items = walked.data;

                return { data: items };
            },
            providesTags: ['Quote'],
        }),

        readQuote: build.query<TypeQuote, string>({
            query: (id) => ({ url: `/quotes/${id}` }),
            providesTags: (_result, _error, id) => [{ type: 'Quote', id }],
        }),

        createQuote: build.mutation<TypeQuote, { quote: TypeQuote; clientRef: string }>({
            query: ({ quote, clientRef }) => ({ url: '/quotes', method: 'post', data: { quote: toInput(quote), clientRef } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeList(dispatch, data);
                    writeDetail(dispatch, data);
                } catch {
                }
            },
        }),

        updateQuote: build.mutation<TypeQuote, { id: string; quote: TypeQuote }>({
            query: ({ id, quote }) => ({ url: `/quotes/${id}`, method: 'patch', data: { quote: toInput(quote) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeList(dispatch, data);
                    writeDetail(dispatch, data);
                } catch {
                }
            },
        }),

        changeQuoteStatus: build.mutation<TypeQuote, { id: string; status: TypeQuoteStatus; note?: string }>({
            query: ({ id, status, note }) => ({ url: `/quotes/${id}/status`, method: 'patch', data: { status, note: note ?? '' } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeList(dispatch, data);
                    writeDetail(dispatch, data);
                } catch {
                }
            },
        }),

        duplicateQuote: build.mutation<TypeQuote, string>({
            query: (id) => ({ url: `/quotes/${id}/duplicate`, method: 'post' }),
            onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeList(dispatch, data);
                    writeDetail(dispatch, data);
                } catch {
                }
            },
        }),

        reviseQuote: build.mutation<TypeQuote, string>({
            query: (id) => ({ url: `/quotes/${id}/revision`, method: 'post' }),
            onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeList(dispatch, data);
                    writeDetail(dispatch, data);
                } catch {
                }
            },
        }),

        changeQuoteStatusMany: build.mutation<TypeQuoteBulkStatusResult, { ids: string[]; status: TypeQuoteStatus; note?: string }>({
            query: ({ ids, status, note }) => ({ url: '/quotes/status', method: 'patch', data: { ids, status, note: note ?? '' } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    for (const quote of data.updated) {
                        writeList(dispatch, quote);
                        writeDetail(dispatch, quote);
                    }
                } catch {
                }
            },
        }),

        removeQuoteMany: build.mutation<TypeQuoteBulkRemoveResult, string[]>({
            query: (ids) => ({ url: '/quotes', method: 'delete', data: { ids } }),
            onQueryStarted: async (_ids, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    if (data.removed.length === 0) return;
                    const gone = new Set(data.removed);
                    dispatch(quoteApi.util.updateQueryData('listQuotes', undefined, (draft) => {
                        for (let index = draft.length - 1; index >= 0; index--) {
                            if (gone.has(draft[index].id)) draft.splice(index, 1);
                        }
                    }));
                } catch {
                }
            },
        }),

        removeQuote: build.mutation<null, string>({
            query: (id) => ({ url: `/quotes/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dispatch(quoteApi.util.updateQueryData('listQuotes', undefined, (draft) => {
                        dropById(draft, id);
                    }));
                } catch {
                }
            },
        }),
    }),
});

const writeList = (dispatch: TypeAppDispatch, quote: TypeQuote) => {
    dispatch(quoteApi.util.updateQueryData('listQuotes', undefined, (draft) => {
        upsertById(draft, quote);
        draft.sort(byRecent);
    }));
};

const writeDetail = (dispatch: TypeAppDispatch, quote: TypeQuote) => {
    dispatch(quoteApi.util.updateQueryData('readQuote', quote.id, () => quote));
};

const toInput = (quote: TypeQuote) => {
    const { id, number, revision, status, history, createdAt, updatedAt, sentAt, decidedAt, ...input } = quote;
    return input;
};

export const {
    useReadQuotePdfMutation,
    useReadQuoteDraftPdfMutation,
    useListQuotesQuery,
    useReadQuoteQuery,
    useCreateQuoteMutation,
    useUpdateQuoteMutation,
    useChangeQuoteStatusMutation,
    useChangeQuoteStatusManyMutation,
    useRemoveQuoteManyMutation,
    useDuplicateQuoteMutation,
    useReviseQuoteMutation,
    useRemoveQuoteMutation,
} = quoteApi;

export default quoteApi;
